// src/pages/ChatIAPage.jsx
import { useState, useEffect, useRef } from "react";
import { Send, Trash2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import apiClient from "../Api/apiConfig";

import NonoImage from "../assets/Nono.png";
import "../styles/ChatIAPage.css";

const STORAGE_KEY = "chatHistory_housing";

export default function ChatIAPage() {
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);

  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const chatBodyRef = useRef(null);

  // 🧑‍⚖️ Accès réservé membres + admin (comme ChatWindow)
  useEffect(() => {
    if (!isAuthenticated || (user?.role !== "admin" && user?.role !== "member")) {
      alert("Accès réservé aux adhérents et administrateurs.");
      navigate("/login");
    }
  }, [isAuthenticated, user, navigate]);

  // 🔄 Chargement historique
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setChatHistory(JSON.parse(saved));
      } catch (err) {
        console.error("Erreur récupération historique :", err);
        setChatHistory([]);
      }
    }
  }, []);

  // 💾 Sauvegarde historique
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(chatHistory));
  }, [chatHistory]);

  // 📜 Scroll auto en bas
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [chatHistory, loading]);

const handleSend = async () => {
  if (!message.trim()) {
    setError("Le message ne peut pas être vide.");
    return;
  }

  setError("");
  const userMessage = { sender: "user", content: message };
  const currentMessage = message;

  setChatHistory((prev) => [...prev, userMessage]);
  setMessage("");
  setLoading(true);

  try {
    // 🔗 APPEL VERS TON VRAI BACKEND : /api/chat (via baseURL + "/chat")
    const response = await apiClient.post("/chat", {
      message: currentMessage,
      // facultatif : si tu veux loguer/adapter côté backend
      userId: user?.id || null,
      context: "assistant logement et locataires",
    });

    // 👉 à adapter selon ce que renvoie addMessage :
    //   - reply
    //   - answer
    //   - message
    const aiText =
      response?.data?.reply ||
      response?.data?.answer ||
      response?.data?.message ||
      "Je n'ai pas pu générer la réponse attendue.";

    setChatHistory((prev) => [
      ...prev,
      { sender: "ai", content: aiText },
    ]);
  } catch (err) {
    console.error("Erreur IA logement :", err);

    const backendMessage =
      err.response?.data?.message ||
      err.response?.data?.error ||
      "Erreur interne du serveur IA.";

    setChatHistory((prev) => [
      ...prev,
      {
        sender: "ai",
        content:
          "Désolé, une erreur s'est produite côté serveur : " +
          backendMessage,
      },
    ]);
  } finally {
    setLoading(false);
  }
};


  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !loading) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = () => {
    if (
      window.confirm(
        "Effacer toute la conversation avec l'assistant logement ?"
      )
    ) {
      setChatHistory([]);
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  return (
    <div className="chatia-page-container">
      <div className="chatia-bg-layer" />

      <div className="chatia-center-column">
        {/* ------- HEADER / INTRO ------- */}
        <header className="chatia-header">
          <span className="chatia-badge">ASSISTANT IA</span>
          <h1>Assistant logement & droits des locataires</h1>
          <p>
            Posez vos questions sur les charges, les travaux, les courriers à
            envoyer ou vos droits face au bailleur et à l&apos;administration.&nbsp;
            <strong>
              L&apos;assistant IA vous aide à y voir plus clair 24h/24.
            </strong>
          </p>

          <div className="chatia-disclaimer">
            ⚖️&nbsp;
            <span>
              Cet outil est un support d&apos;information et d&apos;accompagnement.
              Il ne remplace pas un avocat ni un professionnel du droit.
            </span>
          </div>
        </header>

        {/* ------- CARTE CENTRALE ------- */}
        <section className="chatia-card-wrapper">
          <div className="chatia-card">
            <div className="chatia-card-header">
              <div
                className="chatia-header-avatar"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <img
                  src={NonoImage}
                  alt="Assistant IA"
                  className="chatia-avatar-img"
                />
                <span className="chatia-avatar-status" />
                {showTooltip && (
                  <div className="chatia-avatar-tooltip">
                    Je suis votre assistant logement IA NMS06.
                  </div>
                )}
              </div>

              <div className="chatia-header-texts">
                <h2>Chat IA Logement</h2>
                <p>
                  Expliquez votre situation le plus clairement possible :
                  l&apos;assistant IA vous propose des pistes et des idées de
                  courriers.
                </p>
              </div>

              <button
                type="button"
                className="chatia-clear-btn"
                onClick={handleClearChat}
                title="Effacer la conversation"
              >
                <Trash2 size={18} />
              </button>
            </div>

            {/* ------- ZONE CHAT ------- */}
            <div className="chatia-chat-area">
              <div className="chatia-chat-body" ref={chatBodyRef}>
                {chatHistory.length === 0 && !loading && (
                  <div className="chatia-empty-state">
                    <p>
                      Commencez la discussion en décrivant votre problème :
                      augmentation de charges, travaux, courrier reçu du
                      bailleur, expulsion, etc.
                    </p>
                  </div>
                )}

                {chatHistory.map((entry, index) => (
                  <div
                    key={index}
                    className={`chatia-message ${
                      entry.sender === "user" ? "user" : "ai"
                    }`}
                  >
                    <div className="chatia-message-bubble">
                      {entry.content}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="chatia-message ai">
                    <div className="chatia-message-bubble typing">
                      <span className="typing-dot" />
                      <span className="typing-dot" />
                      <span className="typing-dot" />
                    </div>
                  </div>
                )}
              </div>

              {/* ------- INPUT ------- */}
              <div className="chatia-footer">
                {error && <p className="chatia-error">{error}</p>}

                <div className="chatia-input-row">
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="chatia-input"
                    placeholder="Décrivez votre question ou votre situation..."
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="chatia-send-btn"
                    onClick={handleSend}
                    disabled={loading}
                    aria-label="Envoyer le message"
                  >
                    <Send size={18} />
                  </button>
                </div>
                <p className="chatia-hint">
                  💡 Plus vous donnez de détails (dates, montants, type de
                  logement, bailleur, etc.), plus la réponse sera précise.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
